/*
 * Copyright (c) 2025. World Wide Generation Ltd
 */

import { g17ecoApi, transformResponse } from '@api/g17ecoApi';

export interface DataIntegrationConnection {
  id: string;
  service: string;
  schema: string;
  paused: boolean;
  status: {
    tasks?: {
      code: string;
      message: string;
      details: string;
    }[];
    warnings?: {
      code: string;
      message: string;
      details: string;
    }[];
    schema_status?: string;
    update_state: string;
    setup_state: string;
    sync_state: string;
    sync_frequency: string;
    is_historical_sync: boolean;
    rescheduled_for?: string;
  };
  daily_sync_time?: string;
  succeeded_at?: string;
  sync_frequency: number;
  group_id: string;
  connected_by?: string;
  setup_tests?: {
    title: string;
    status: string;
    message: string;
    details: string;
  }[];
  source_sync_details?: object;
  service_version: number;
  created_at: string;
  failed_at: string;
  data_delay_threshold?: number;
  data_delay_sensitivity: 'LOW' | 'NORMAL' | 'HIGH' | 'CUSTOM' | 'SYNC_FREQUENCY';
  schedule_type: string;
  hybrid_deployment_agent_id?: string;
}

export interface DataIntegrationConnector {
  id: string;
  name: string;
  type: 'API' | 'Dbt' | 'Marketing' | 'HumanResources' | 'Finance' | 'Productivity' | 'Engineering' | 'Support' | 'Sales' | 'Security' | 'SuperConnectorTest' | 'Free' | 'File' | 'Database' | 'Events' | 'Function' | 'BITool' | 'Warehouse' | 'Log' | 'Hvr';
  description?: string;
  icons?: string[];
  config?: object;
  status: 'GENERALLY_AVAILABLE' | 'BETA' | 'ALPHA';
  service_status_updated_at: string;
  supported_features?: {
    id: 'CUSTOM_DATA' | 'CAPTURE_DELETES' | 'DATA_BLOCKING' | 'COLUMN_HASHING' | 'RE_SYNC' | 'HISTORY' | 'API_CONFIGURABLE' | 'PRIORITY_FIRST_SYNC' | 'FIVETRAN_DATA_MODELS' | 'PRIVATE_NETWORKING' | 'AUTHORIZATION_VIA_API';
    notes: string;
  }[];
  link_to_docs?: string;
  connector_class?: 'standard' | 'lite';
  link_to_erd?: string;
  service_status?: 'general_availability' | 'beta' | 'private_preview' | 'sunset' | 'development';
  icon_url?: string;
}

interface ConnectCardResponse {
  connector_id: string;
  connect_card: {
    token: string;
    uri: string;
  };
  connect_card_config: {
    redirect_uri: string;
    hide_setup_guide: boolean;
  };
}

// --- DataIntegration Schema Config API Types ---
interface DataIntegrationSchemaConfigResponse {
  enable_new_by_default: boolean;
  schema_change_handling: 'ALLOW_ALL' | 'ALLOW_COLUMNS' | 'BLOCK_ALL';
  schemas: {
    [schemaName: string]: {
      name_in_destination: string;
      enabled: boolean;
      tables: {
        [tableName: string]: {
          sync_mode?: 'SOFT_DELETE' | 'HISTORY' | 'LIVE';
          name_in_destination: string;
          enabled: boolean;
          columns: {
            [columnName: string]: {
              name_in_destination: string;
              enabled: boolean;
              hashed: boolean;
              enabled_patch_settings: {
                allowed: boolean;
                reason?: string;
                reason_code?: 'SYSTEM_COLUMN' | 'DELETED' | 'OTHER';
              };
              is_primary_key?: boolean;
            };
          };
          enabled_patch_settings: {
            allowed: boolean;
            reason?: string;
            reason_code?: 'SYSTEM_TABLE' | 'DELETED' | 'OTHER';
          };
          supports_columns_config: boolean;
        };
      };
    };
  };
}

export interface SnowflakeDatabaseSchema {
  database_name: string;
  schemas: {
    [schemaName: string]: {
      tables: {
        [tableName: string]: {
          columns: {
            [columnName: string]: {
              data_type: string;
              is_nullable: boolean;
              description?: string;
            };
          };
          description?: string;
        };
      };
    };
  };
}

export interface SemanticModel {
  name: string;
  description?: string;
  custom_instructions?: string;
  module_custom_instructions?: {
    question_categorization?: string;
    sql_generation?: string;
  };
  tables: SemanticModelLogicalTable[];
  relationships?: SemanticModelRelationship[];
}

export interface SemanticModelLogicalTable {
  name: string;
  base_table: {
    database: string;
    schema: string;
    table: string;
  };
  description?: string;
  synonyms?: string[];
  primary_key?: { columns: string[] };
  dimensions?: SemanticModelDimension[];
  time_dimensions?: SemanticModelTimeDimension[];
  facts?: SemanticModelFact[];
  metrics?: SemanticModelMetric[];
  filters?: SemanticModelFilter[];
}

interface SemanticModelColumnBase {
  name: string;
  synonyms?: string[];
  description?: string;
  expr: string;
  data_type?: string;
}

export interface SemanticModelDimension extends SemanticModelColumnBase {
  unique?: boolean;
}

export interface SemanticModelTimeDimension extends SemanticModelColumnBase {
  unique?: boolean;
}

export type SemanticModelFact = SemanticModelColumnBase;

interface SemanticModelMetric {
  name: string;
  expr: string;         // e.g. "SUM(amount * (1 - discount))"
  data_type: string;
  description?: string;
  synonyms?: string[];
}

interface SemanticModelFilter {
  name: string;
  expr: string;         // e.g. "status != 'cancelled'"
  synonyms?: string[];
  description?: string;
}

export interface SemanticModelRelationship {
  name: string;
  left_table: string;
  right_table: string;
  relationship_columns: {
    left_column: string;
    right_column: string;
  }[];
  join_type: 'left_outer' | 'inner';
  relationship_type: 'many_to_one' | 'one_to_one';
}

export interface AIChatQueryResponse {
  conversation_id?: string;
  response: string;
  sql_query?: string;
  data?: { [key: string]: string | number | null }[];
  suggested_questions?: string[];
  warnings?: Array<{
    message: string;
  }>;
  response_metadata: {
    model_names: string[];
    is_sematic_sql: boolean;
    cortex_search_retrieval: Record<string, unknown>;
    question_category: string;
  };
}

const dataIntegrationsApi = g17ecoApi
  .enhanceEndpoints({
    addTagTypes: ['DataIntegrationConnectors', 'DataIntegrationConnections', 'SnowflakeSemanticModel', 'IntegrationStatus'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      // List available connectors
      listDataIntegrationConnectors: builder.query<DataIntegrationConnector[], { initiativeId: string }>({
        transformResponse,
        providesTags: ['DataIntegrationConnectors'],
        query: ({ initiativeId }) => ({
          url: `/initiatives/${initiativeId}/data-integration/fivetran/connectors`,
          method: 'get',
        }),
      }),

      // List existing Connections
      listDataIntegrationConnections: builder.query<DataIntegrationConnection[], { initiativeId: string }>({
        transformResponse,
        providesTags: ['DataIntegrationConnections'],
        query: ({ initiativeId }) => ({
          url: `/initiatives/${initiativeId}/data-integration/fivetran/connections`,
          method: 'get',
        }),
      }),

      // Delete Connection
      deleteDataIntegrationConnection: builder.mutation<void, { initiativeId: string; connectionId: string }>({
        invalidatesTags: ['DataIntegrationConnections'],
        query: ({ initiativeId, connectionId }) => ({
          url: `/initiatives/${initiativeId}/data-integration/fivetran/connections/${connectionId}`,
          method: 'delete',
        }),
      }),

      // Embedded workflow - create session
      createConnectCard: builder.mutation<
        ConnectCardResponse,
        { initiativeId: string; connectorId: string; redirectUri: string }
      >({
        transformResponse,
        query: ({ initiativeId, connectorId, redirectUri }) => ({
          url: `/initiatives/${initiativeId}/data-integration/fivetran/connect-card`,
          method: 'post',
          data: { connectorId, redirectUri },
        }),
      }),

      // Pause Connection
      pauseDataIntegrationConnection: builder.mutation<void, { initiativeId: string; connectionId: string }>({
        invalidatesTags: ['DataIntegrationConnections'],
        query: ({ initiativeId, connectionId }) => ({
          url: `/initiatives/${initiativeId}/data-integration/fivetran/connections/${connectionId}/pause`,
          method: 'patch',
        }),
      }),

      // Resume Connection
      resumeDataIntegrationConnection: builder.mutation<void, { initiativeId: string; connectionId: string }>({
        invalidatesTags: ['DataIntegrationConnections'],
        query: ({ initiativeId, connectionId }) => ({
          url: `/initiatives/${initiativeId}/data-integration/fivetran/connections/${connectionId}/resume`,
          method: 'patch',
        }),
      }),

      // Sync Connection
      syncDataIntegrationConnection: builder.mutation<void, { initiativeId: string; connectionId: string }>({
        invalidatesTags: ['DataIntegrationConnections'],
        query: ({ initiativeId, connectionId }) => ({
          url: `/initiatives/${initiativeId}/data-integration/fivetran/connections/${connectionId}/sync`,
          method: 'post',
        }),
      }),

      // Resync Connection
      resyncDataIntegrationConnection: builder.mutation<void, { initiativeId: string; connectionId: string }>({
        invalidatesTags: ['DataIntegrationConnections'],
        query: ({ initiativeId, connectionId }) => ({
          url: `/initiatives/${initiativeId}/data-integration/fivetran/connections/${connectionId}/resync`,
          method: 'post',
        }),
      }),

      // Test Connection
      testDataIntegrationConnection: builder.mutation<void, { initiativeId: string; connectionId: string }>({
        query: ({ initiativeId, connectionId }) => ({
          url: `/initiatives/${initiativeId}/data-integration/fivetran/connections/${connectionId}/test`,
          method: 'post',
        }),
      }),

      // Get DataIntegration Connection Schema Config (all schemas/tables/columns)
      getDataIntegrationConnectionSchemaConfig: builder.query<DataIntegrationSchemaConfigResponse, { initiativeId: string; connectionId: string }>({
        transformResponse,
        providesTags: (result, error, { initiativeId, connectionId }) => [
          { type: 'DataIntegrationConnections', id: `${initiativeId}-${connectionId}-schema` }
        ],
        query: ({ initiativeId, connectionId }) => ({
          url: `/initiatives/${initiativeId}/data-integration/fivetran/connections/${connectionId}/schemas`,
          method: 'get',
        }),
      }),

      // Update Table Sync Status
      updateTableSyncStatus: builder.mutation<void, {
        initiativeId: string;
        connectionId: string;
        schemaName: string;
        tableName: string;
        enabled: boolean
      }>({
        invalidatesTags: (result, error, { initiativeId, connectionId }) => [
          { type: 'DataIntegrationConnections', id: `${initiativeId}-${connectionId}-schema` }
        ],
        query: ({ initiativeId, connectionId, schemaName, tableName, enabled }) => ({
          url: `/initiatives/${initiativeId}/data-integration/fivetran/connections/${connectionId}/schemas/${schemaName}/tables/${tableName}`,
          method: 'patch',
          data: { enabled },
        }),
      }),

      // Get Snowflake Database Schema
      getSnowflakeDatabaseSchema: builder.query<SnowflakeDatabaseSchema, { initiativeId: string; connectionId: string }>({
        transformResponse,
        query: ({ initiativeId, connectionId }) => ({
          url: `/initiatives/${initiativeId}/data-integration/fivetran/connections/${connectionId}/snowflake/schemas`,
          method: 'get',
        }),
      }),

      // Get Snowflake Semantic Models
      getSnowflakeSemanticModel: builder.query<SemanticModel, { initiativeId: string; connectionId: string }>({
        transformResponse,
        query: ({ initiativeId, connectionId }) => ({
          url: `/initiatives/${initiativeId}/data-integration/fivetran/connections/${connectionId}/snowflake/semantic-model`,
          method: 'get',
        }),
        providesTags: (result, error, { initiativeId, connectionId }) => [
          { type: 'SnowflakeSemanticModel', id: `${initiativeId}-${connectionId}` }
        ],
      }),

      // Save Snowflake Semantic Model
      saveSnowflakeSemanticModel: builder.mutation<SemanticModel, { initiativeId: string; connectionId: string; yamlContent: object }>({
        transformResponse,
        query: ({ initiativeId, connectionId, yamlContent }) => ({
          url: `/initiatives/${initiativeId}/data-integration/fivetran/connections/${connectionId}/snowflake/semantic-model`,
          method: 'post',
          data: { yamlContent },
        }),
        invalidatesTags: (result, error, { initiativeId, connectionId }) => [
          { type: 'SnowflakeSemanticModel', id: `${initiativeId}-${connectionId}` }
        ],
      }),

      // Send AI Chat Query
      sendAIChat: builder.mutation<AIChatQueryResponse, { initiativeId: string; message?: string; conversationId?: string; context?: object }>({
        transformResponse,
        query: ({ initiativeId, message, conversationId, context }) => ({
          url: `/initiatives/${initiativeId}/data-integration/chat`,
          method: 'post',
          data: { message, conversationId, context },
        }),
      }),

      // Enable DataIntegration
      enableDataIntegration: builder.mutation<void, { initiativeId: string }>({
        invalidatesTags: ['IntegrationStatus'],
        query: ({ initiativeId }) => ({
          url: `/initiatives/${initiativeId}/data-integration/fivetran/enable`,
          method: 'post',
        }),
      }),

      // Get DataIntegration Status
      getDataIntegrationStatus: builder.query<{ enabled: boolean }, { initiativeId: string }>({
        transformResponse,
        providesTags: ['IntegrationStatus'],
        query: ({ initiativeId }) => ({
          url: `/initiatives/${initiativeId}/data-integration/fivetran/enable`,
          method: 'get',
        }),
      }),
    }),
  });

export const {
  useListDataIntegrationConnectorsQuery,
  useListDataIntegrationConnectionsQuery,
  useDeleteDataIntegrationConnectionMutation,
  useCreateConnectCardMutation,
  usePauseDataIntegrationConnectionMutation,
  useResumeDataIntegrationConnectionMutation,
  useSyncDataIntegrationConnectionMutation,
  useResyncDataIntegrationConnectionMutation,
  useTestDataIntegrationConnectionMutation,
  useGetDataIntegrationConnectionSchemaConfigQuery,
  useUpdateTableSyncStatusMutation,
  useGetSnowflakeSemanticModelQuery,
  useGetSnowflakeDatabaseSchemaQuery,
  useSaveSnowflakeSemanticModelMutation,
  useSendAIChatMutation,
  useEnableDataIntegrationMutation,
  useGetDataIntegrationStatusQuery,
} = dataIntegrationsApi;
